import React, { useMemo, useState } from 'react'
import {
	Box,
	Dialog,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	IconButton,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { useModal } from '../../../../../store/ModalProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import type { VehicleWashModalData, VehicleWashRow } from '../../../../../types/modal'

type DensityMode = 'compact' | 'standard'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const defaultRows: VehicleWashRow[] = Array.from({ length: 13 }, (_val, index) => ({
	id: `wash-${index + 1}`,
	viatura: '0071',
	ultimoServico: 'sex 02/05/2024 17:30',
	origemDestino: 'Lisboa Porto',
	diaSeguinte: 'Não',
	primeiraSaida: 'dom 12/05/2024 10:00',
	ultimaLavagem: '',
	comentarios: '',
	pernoita: 'Portimão (Oficina)',
	ultimaAspiracao: '',
}))

const CustomModalVehicleWash: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as VehicleWashModalData | undefined
	const [handlePrint, handleSend] = modalHandlers ?? []

	const [date, setDate] = useState<Dayjs | null>(
		data?.date ? dayjs(data.date, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const [overnightLocation, setOvernightLocation] = useState(data?.overnightLocation ?? 'Todos')
	const [density, setDensity] = useState<DensityMode>('compact')
	const [page, setPage] = useState(0)

	const rows = useMemo(() => (data?.rows?.length ? data.rows : defaultRows), [data])
	const rowsPerPage = data?.rowsPerPage ?? 10
	const totalVehicles = data?.totalVehicles ?? 50
	const pageCount = Math.max(1, Math.ceil(rows.length / rowsPerPage))
	const safePage = Math.min(page, pageCount - 1)
	const startIndex = safePage * rowsPerPage
	const pageRows = rows.slice(startIndex, startIndex + rowsPerPage)
	const rangeEnd = Math.min(startIndex + pageRows.length, rows.length)
	const pageLabel = rows.length > 0 ? `${startIndex + 1}-${rangeEnd} de ${rows.length}` : '0-0 de 0'

	const columns = useMemo<GridColDef<VehicleWashRow>[]>(
		() => [
			{ field: 'viatura', headerName: 'Viatura', width: 70 },
			{
				field: 'ultimoServico',
				headerName: 'Últ. Serviço',
				width: 140,
			},
			{
				field: 'origemDestino',
				headerName: 'Origem Destino',
				width: 120,
			},
			{
				field: 'diaSeguinte',
				headerName: 'Dia Seguinte',
				width: 95,
			},
			{ field: 'primeiraSaida', headerName: '1 Saída', width: 120 },
			{
				field: 'ultimaLavagem',
				headerName: 'Últ. Lav. Geral',
				width: 120,
			},
			{ field: 'comentarios', headerName: 'Comentários', width: 110 },
			{ field: 'pernoita', headerName: 'Pernoita', width: 150 },
			{
				field: 'ultimaAspiracao',
				headerName: 'Últ. Aspiração',
				width: 120,
			},
		],
		[],
	)

	const handleClearFilters = () => {
		setDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setOvernightLocation('Todos')
	}

	const handlePrintWash = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(pageRows)
			return
		}
		console.log('Imprimir lavagem de viaturas', pageRows)
	}

	const handleSendWash = () => {
		if (typeof handleSend === 'function') {
			handleSend(pageRows)
			return
		}
		console.log('Enviar lavagem de viaturas', pageRows)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box sx={{ padding: '16px 24px' }}>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Lavagem de Viaturas
					</Typography>
				</Box>

				<Box sx={{ padding: '0 24px 24px', display: 'grid', gap: '24px' }}>
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'center',
							gap: '16px',
						}}
					>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label='Data da Escala'
								value={date}
								onChange={newValue => setDate(newValue)}
								format='DD/MM/YYYY'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { minWidth: '160px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
							/>
						</LocalizationProvider>
						<TextField
							select
							label='Local de Pernoita'
							value={overnightLocation}
							onChange={event => setOvernightLocation(event.target.value)}
							size='small'
							sx={{ ...inputSx, minWidth: '180px' }}
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
						>
							<MenuItem value='Todos'>Todos</MenuItem>
							<MenuItem value='Portimão (Oficina)'>Portimão (Oficina)</MenuItem>
						</TextField>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleClearFilters}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '12px',
								textTransform: 'none',
								marginLeft: 'auto',
								padding: '4px 0',
								'&:hover': { backgroundColor: 'transparent' },
							}}
						>
							Limpar Filtros
						</CustomButton>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Typography
							sx={{
								fontSize: '24px',
								fontWeight: 600,
								lineHeight: 1.334,
								color: '#092056',
							}}
						>
							{totalVehicles} Viaturas
						</Typography>
						<ToggleButtonGroup
							value={density}
							exclusive
							onChange={(_, value) => value && setDensity(value)}
							size='small'
							sx={toggleGroupSx}
						>
							<ToggleButton
								value='compact'
								aria-label='densidade compacta'
							>
								<TableRowsIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
							<ToggleButton
								value='standard'
								aria-label='densidade espaçada'
							>
								<ViewHeadlineIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<AnimatedDataGridPro
						slots={{ columnMenu: CustomColumnMenu }}
						slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
						rows={pageRows}
						columns={columns}
						density={density}
						rowHeight={density === 'compact' ? 32 : 40}
						columnHeaderHeight={40}
						autoHeight
						checkboxSelection
						disableRowSelectionOnClick
						hideFooter
						sx={[tableSx, dataGridDensityTransitionSx]}
					/>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '26px',
							marginTop: '-8px',
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
							<Typography
								sx={{
									fontSize: '11px',
									fontWeight: 400,
									color: '#8695AF',
									lineHeight: 1.3,
								}}
							>
								Linhas por página
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
								<Typography
									sx={{
										fontSize: '11px',
										fontWeight: 400,
										color: '#092056',
										lineHeight: 1.3,
									}}
								>
									{rowsPerPage}
								</Typography>
								<KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
							</Box>
						</Box>
						<Typography
							sx={{
								fontSize: '11px',
								fontWeight: 400,
								color: '#092056',
								lineHeight: 1.3,
							}}
						>
							{pageLabel}
						</Typography>
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<CustomButton
								variant='text'
								onClick={() => setPage(prev => Math.max(prev - 1, 0))}
								sx={{ minWidth: 'auto', padding: '4px' }}
							>
								<ChevronLeftRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
							</CustomButton>
							<CustomButton
								variant='text'
								onClick={() => setPage(prev => Math.min(prev + 1, pageCount - 1))}
								sx={{ minWidth: 'auto', padding: '4px' }}
							>
								<ChevronRightRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
							</CustomButton>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '8px',
						padding: '8px 24px 16px',
					}}
				>
					<CustomButton
						variant='text'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='text'
						onClick={handlePrintWash}
						sx={popupSecondaryButtonSx}
					>
						Imprimir
					</CustomButton>
					<CustomButton
						variant='contained'
						color='primary'
						onClick={handleSendWash}
						sx={popupActionButtonSx}
					>
						Enviar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalVehicleWash
